import { useEffect, useState, useCallback, useRef } from 'react';
import { subDays, subMonths } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';

import { request } from 'modules/Api/HttpClient';
import { DASHBOARD_MISSING_VIDEOS_URL } from 'modules/Api/Routes';

export const PER_PAGE = 10;

const STORAGE_KEY = 'missing_videos_widget_filters';

const readStoredFilters = (widgetId) => {
  try {
    return JSON.parse(localStorage.getItem(`${STORAGE_KEY}_${widgetId}`));
  } catch (e) {
    return null;
  }
};

const storeFilters = (widgetId, filters) => {
  try {
    localStorage.setItem(`${STORAGE_KEY}_${widgetId}`, JSON.stringify(filters));
  } catch (e) {
    // Persisting is a convenience; the widget works without it.
  }
};

// Same options as IntervalOptionsMenu: '12' means months, the others days.
const intervalStart = (interval, now) =>
  interval === '12' ? subMonths(now, 12) : subDays(now, Number(interval));

// The API reads start_date/end_date as calendar days in `timezone`.
const toProfileDay = (date, timezone) =>
  formatInTimeZone(new Date(date), timezone, 'yyyy-MM-dd');

export const useMissingVideosWidget = ({
  enabled,
  timezone,
  widgetId,
  defaultInterval,
}) => {
  const [filters, setFilters] = useState(() => {
    const stored = widgetId ? readStoredFilters(widgetId) : null;
    return {
      interval: stored?.interval || defaultInterval || '7',
      missingType: stored?.missingType || '',
      page: 1,
    };
  });
  const [state, setState] = useState({
    loading: true,
    loaded: false,
    error: null,
    sessions: [],
    hasMore: false,
  });
  const loadingRef = useRef(true);

  useEffect(() => {
    if (!enabled) return undefined;

    let isStale = false;
    const { interval, missingType, page } = filters;
    const zone = timezone || 'UTC';
    const now = new Date();

    loadingRef.current = true;
    setState((previous) => ({ ...previous, loading: true, error: null }));

    request({
      method: 'GET',
      url: DASHBOARD_MISSING_VIDEOS_URL,
      params: {
        start_date: toProfileDay(intervalStart(interval, now), zone),
        end_date: toProfileDay(now, zone),
        timezone: zone,
        ...(missingType && { missing_type: missingType }),
        page,
        paginates_per: PER_PAGE,
      },
    })
      .then((response) => {
        if (isStale) return;
        const { sessions = [], last_page: lastPage } = response.data.content;
        loadingRef.current = false;
        setState((previous) => {
          const known = new Set(previous.sessions.map(({ id }) => id));
          return {
            loading: false,
            loaded: true,
            error: null,
            sessions:
              page === 1
                ? sessions
                : [
                    ...previous.sessions,
                    ...sessions.filter(({ id }) => !known.has(id)),
                  ],
            hasMore: !lastPage,
          };
        });
      })
      .catch((error) => {
        if (isStale) return;
        loadingRef.current = false;
        setState((previous) => ({
          ...previous,
          loading: false,
          loaded: true,
          hasMore: false,
          error: page === 1 ? error?.message || 'error' : null,
          sessions: page === 1 ? [] : previous.sessions,
        }));
      });

    return () => {
      isStale = true;
    };
  }, [enabled, timezone, filters]);

  useEffect(() => {
    if (widgetId) {
      storeFilters(widgetId, {
        interval: filters.interval,
        missingType: filters.missingType,
      });
    }
  }, [widgetId, filters.interval, filters.missingType]);

  const changeFilters = useCallback(
    (changes) => setFilters((f) => ({ ...f, page: 1, ...changes })),
    []
  );

  const loadMore = useCallback(() => {
    if (loadingRef.current) return;
    loadingRef.current = true;
    setFilters((f) => ({ ...f, page: f.page + 1 }));
  }, []);

  return { filters, state, changeFilters, loadMore };
};
